import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Empty } from '@/components/ui/empty';
import { ListSkeleton } from '@/components/PageSkeleton';
import { ErrorState, Field, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { ConfirmAction } from '@/components/confirm';
import AccountDialog from '@/components/AccountDialog';
import MakeAdminDialog, { initialsOf } from '@/components/admin/MakeAdminDialog';
import {
  ROLE_HINTS,
  ROLE_LABELS,
  ROLE_PROFILES,
  ROLE_STYLES,
  TEAM_PROFILES,
  isTeamMember,
  loadAllMembers,
  memberName,
  setMemberRole,
} from '@/shared/team';
import { logActivity } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/team', layout: 'owner', access: "profile === 'Admin_c'" };
export const nav = { icon: 'UsersRound', label: 'Team', section: 'Owner', profiles: ['Admin_c'], order: 95 };

const errText = (err, fallback) => err?.message || fallback;

async function loadInvites() {
  const res = await sdk.teams.getInvitedMembers(0, 100);
  return res?.data ?? [];
}

function InviteDialog({ open, onOpenChange, onSent }) {
  const [email, setEmail] = useState('');
  const [profile, setProfile] = useState('Staff_c');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail('');
      setProfile('Staff_c');
    }
  }, [open]);

  async function submit() {
    const address = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(address)) {
      toast.error('Add a valid email address');
      return;
    }
    setSaving(true);
    try {
      await sdk.teams.inviteUsers({ invitations: [{ email: address, profileName: profile }] });
      toast.success(`Invitation sent to ${address}`);
      logActivity('Team', 'Invited', address, ROLE_LABELS[profile]);
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      toast.error(errText(err, 'Could not send the invitation'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Invite to the team"
      description="For someone without a Ghost Cable sign-in. They get an email with a link to set a password."
      onSubmit={submit}
      saving={saving}
      submitLabel="Send invitation"
      busyLabel="Sending…"
    >
      <Field label="Email" htmlFor="tm-email">
        <Input id="tm-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="rowan@ghostcable.app" />
      </Field>
      <Field label="Role" htmlFor="tm-role" hint={ROLE_HINTS[profile]}>
        <PickSelect id="tm-role" value={profile} onChange={setProfile} values={TEAM_PROFILES} labels={ROLE_LABELS} placeholder="Choose a role" />
      </Field>
    </FormDialog>
  );
}

function RoleDialog({ member, onOpenChange, onSaved }) {
  const [profile, setProfile] = useState(member?.profile?.name ?? 'Staff_c');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) setProfile(member.profile?.name ?? 'Staff_c');
  }, [member]);

  async function submit() {
    if (profile === member.profile?.name) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      await setMemberRole(member, profile);
      toast.success(
        profile === 'Customer_c'
          ? `${memberName(member)} is a customer again`
          : `${memberName(member)} is now ${ROLE_LABELS[profile]}`,
      );
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(errText(err, 'Could not change the role'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={!!member}
      onOpenChange={onOpenChange}
      title={`Change role for ${member?.name || member?.email || ''}`}
      onSubmit={submit}
      saving={saving}
      submitLabel="Change role"
      busyLabel="Changing…"
    >
      <Field label="Role" htmlFor="tm-change-role" hint={ROLE_HINTS[profile]}>
        <PickSelect
          id="tm-change-role"
          value={profile}
          onChange={setProfile}
          values={ROLE_PROFILES}
          labels={ROLE_LABELS}
          placeholder="Choose a role"
        />
      </Field>
    </FormDialog>
  );
}

function Section({ loading, data, error, run, emptyIcon, emptyTitle, emptyText, children }) {
  if (loading && data === undefined) return <ListSkeleton rows={3} />;
  if (error) return <ErrorState message={error} onRetry={run} />;
  if (!data?.length) return <Empty icon={emptyIcon} title={emptyTitle} description={emptyText} />;
  return (
    <ul aria-busy={loading} className="divide-y divide-border">
      {children}
    </ul>
  );
}

export default function AdminTeam() {
  const { user } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [roleFor, setRoleFor] = useState(null);
  const [editFor, setEditFor] = useState(null);

  const everyone = useFetch(loadAllMembers, []);
  const invites = useFetch(loadInvites, []);

  const all = everyone.data;
  const team = all === undefined ? undefined : all.filter(isTeamMember);
  const candidates = (all ?? []).filter((m) => !isTeamMember(m) && m?.profile?.name !== 'PublicProfile');

  const isMe = (m) =>
    (user?.emailAddress && String(m.email).toLowerCase() === String(user.emailAddress).toLowerCase()) ||
    (user?.userId != null && String(m.id) === String(user.userId));

  function openEdit(m) {
    const me = isMe(m);
    setEditFor({
      self: me,
      target: me
        ? {
            id: user?.userId ?? m.id,
            email: user?.emailAddress || m.email,
            firstName: user?.firstName,
            lastName: user?.lastName,
            name: m.name,
          }
        : { id: m.id, email: m.email, name: m.name },
    });
  }

  async function removeMember(m) {
    try {
      await sdk.teams.deleteMember(m.id);
      toast.success(`${memberName(m)} removed from the team`);
      logActivity('Team', 'Removed', m.email, ROLE_LABELS[m.profile?.name] ?? '');
      everyone.run();
      return true;
    } catch (err) {
      toast.error(errText(err, 'Could not remove them'));
      return false;
    }
  }

  async function resend(inv) {
    try {
      await sdk.teams.resendInvitation(inv.invitationId);
      toast.success(`Invitation sent again to ${inv.invitedEmail}`);
      invites.run();
      return true;
    } catch (err) {
      toast.error(errText(err, 'Could not resend it'));
      return false;
    }
  }

  async function revoke(inv) {
    try {
      await sdk.teams.deleteInvitation(inv.invitationId);
      toast.success('Invitation withdrawn');
      logActivity('Team', 'Invitation withdrawn', inv.invitedEmail);
      invites.run();
      return true;
    } catch (err) {
      toast.error(errText(err, 'Could not withdraw it'));
      return false;
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        eyebrow="Owner"
        title="Team"
        description="The people who run Ghost Cable with you. Make any signed-up user an admin, or invite someone new."
        actions={
          <>
            <Button onClick={() => setPromoteOpen(true)} disabled={all === undefined && everyone.loading}>
              <ApperIcon name="ShieldPlus" size={16} />
              Make a user admin
            </Button>
            <Button variant="outline" onClick={() => setInviteOpen(true)}>
              <ApperIcon name="UserPlus" size={16} />
              Invite
            </Button>
          </>
        }
      />

      <Panel title="Members" icon="UsersRound" className="mb-4">
        <Section
          loading={everyone.loading}
          data={team}
          error={everyone.error}
          run={everyone.run}
          emptyIcon="UsersRound"
          emptyTitle="Just you"
          emptyText="Make a user admin, or invite someone to help with orders and tickets."
        >
          {(team ?? []).map((m) => {
            const me = isMe(m);
            const role = me && m.profile?.name === 'Admin_c' ? 'Owner' : (ROLE_LABELS[m.profile?.name] ?? m.profile?.name);
            return (
              <li key={m.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar className="size-9">
                    <AvatarFallback className="bg-muted text-xs">{initialsOf(m.name || m.email)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {m.name || m.email}
                      {me && <span className="ml-2 text-xs text-muted-foreground">(You)</span>}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge value={role} styles={ROLE_STYLES} />
                  <Button variant="outline" size="sm" onClick={() => openEdit(m)}>
                    <ApperIcon name="UserPen" size={16} />
                    Edit details
                  </Button>
                  <Button variant="outline" size="sm" disabled={me} onClick={() => setRoleFor(m)}>
                    <ApperIcon name="ShieldCheck" size={16} />
                    Change role
                  </Button>
                  <ConfirmAction
                    label="Remove"
                    icon="UserMinus"
                    destructive
                    disabled={me}
                    title={`Remove ${memberName(m)}?`}
                    description="They lose access to the console straight away. To keep them as a customer instead, use Change role and pick Customer."
                    busyLabel="Removing…"
                    onConfirm={() => removeMember(m)}
                  />
                </div>
              </li>
            );
          })}
        </Section>
      </Panel>

      <Panel title="Pending invitations" icon="MailPlus">
        <Section
          loading={invites.loading}
          data={invites.data}
          error={invites.error}
          run={invites.run}
          emptyIcon="MailCheck"
          emptyTitle="No pending invitations"
          emptyText="Everyone you invited has joined."
        >
          {(invites.data ?? []).map((inv) => (
            <li key={inv.invitationId} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{inv.invitedEmail}</p>
                <p className="text-xs text-muted-foreground">
                  Invited {inv.createdOn ? formatLocalDate(inv.createdOn, DATE_FORMATS.SHORT) : 'recently'}
                  {inv.invitationCount > 1 ? ` · sent ${inv.invitationCount} times` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <ConfirmAction
                  label="Resend"
                  icon="Send"
                  title={`Resend to ${inv.invitedEmail}?`}
                  description="They get a fresh copy of the invitation email."
                  busyLabel="Sending…"
                  onConfirm={() => resend(inv)}
                />
                <ConfirmAction
                  label="Withdraw"
                  icon="X"
                  destructive
                  title="Withdraw this invitation?"
                  description="The link in their email stops working."
                  busyLabel="Withdrawing…"
                  onConfirm={() => revoke(inv)}
                />
              </div>
            </li>
          ))}
        </Section>
      </Panel>

      <MakeAdminDialog open={promoteOpen} onOpenChange={setPromoteOpen} candidates={candidates} onSaved={everyone.run} />
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onSent={invites.run} />
      <RoleDialog member={roleFor} onOpenChange={(o) => !o && setRoleFor(null)} onSaved={everyone.run} />
      <AccountDialog
        target={editFor?.target ?? null}
        self={!!editFor?.self}
        onOpenChange={(o) => !o && setEditFor(null)}
        onSaved={everyone.run}
      />
    </div>
  );
}
